import React, { useMemo, useCallback, useReducer } from "react";
import useTables from "./hooks/useTables";
import TableCard from "./components/TableCard";
import CustomerDetailsForm from "./components/CustomerDetailsForm";
import NewOrderPage from "./components/NewOrderPage";
import CartOverlay from "./components/CartOverlay";
import CartPage from "./components/CartPage";
import MoveTableModal from "./components/MoveTableModal";
import TableOrderDetails from "./components/TableOrderDetails";
import OrderWithBillGen from "../OrderWithBillGen";
import { BasicTable, Table } from "./types";
import { FaSync, FaUtensils } from "react-icons/fa";
import { saveCustomerDetails } from "../../api/tableApi";

type CustomerDetails = {
  id?: string;
  customerName?: string;
  customerEmail?: string;
  customerMobile?: string;
  customerCount?: number;
  waiterName?: string;
};

type CartItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  instructions?: string;
};

type ViewState = "home" | "new-order" | "cart" | "table-orders" | "table-bill";
type FilterType = "all" | "available" | "occupied";

type State = {
  selectedTableId: string | null;
  currentView: ViewState;
  previousView: ViewState;
  customerDetails: CustomerDetails | null;
  cartItems: CartItem[];
  filter: FilterType;
  openCustomerForm: boolean;
  openMoveModal: boolean;
  moveLoading: boolean;
  fullTableDetails: { [key: string]: Table };
};

type Action =
  | { type: "SET_SELECTED_TABLE"; payload: string | null }
  | { type: "SET_VIEW"; payload: ViewState }
  | { type: "SET_PREVIOUS_VIEW"; payload: ViewState }
  | { type: "SET_CUSTOMER_DETAILS"; payload: CustomerDetails | null }
  | { type: "SET_CART_ITEMS"; payload: CartItem[] }
  | { type: "SET_FILTER"; payload: FilterType }
  | { type: "SET_OPEN_CUSTOMER_FORM"; payload: boolean }
  | { type: "SET_OPEN_MOVE_MODAL"; payload: boolean }
  | { type: "SET_MOVE_LOADING"; payload: boolean }
  | { type: "SET_FULL_TABLE_DETAILS"; payload: { [key: string]: Table } };

const initialState: State = {
  selectedTableId: null,
  currentView: "home",
  previousView: "home",
  customerDetails: null,
  cartItems: [],
  filter: "all",
  openCustomerForm: false,
  openMoveModal: false,
  moveLoading: false,
  fullTableDetails: {},
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SELECTED_TABLE":
      return { ...state, selectedTableId: action.payload };
    case "SET_VIEW":
      return { ...state, currentView: action.payload };
    case "SET_PREVIOUS_VIEW":
      return { ...state, previousView: action.payload };
    case "SET_CUSTOMER_DETAILS":
      return { ...state, customerDetails: action.payload };
    case "SET_CART_ITEMS":
      return { ...state, cartItems: action.payload };
    case "SET_FILTER":
      return { ...state, filter: action.payload };
    case "SET_OPEN_CUSTOMER_FORM":
      return { ...state, openCustomerForm: action.payload };
    case "SET_OPEN_MOVE_MODAL":
      return { ...state, openMoveModal: action.payload };
    case "SET_MOVE_LOADING":
      return { ...state, moveLoading: action.payload };
    case "SET_FULL_TABLE_DETAILS":
      return { ...state, fullTableDetails: action.payload };
    default:
      return state;
  }
}

const QuickServe: React.FC<{ restaurantGuid: string }> = ({ restaurantGuid }) => {
  const { tables, loading, error, refresh, moveTable } = useTables();
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectedTable = state.selectedTableId ? state.fullTableDetails[state.selectedTableId] || null : null;
  const cartItemCount = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotalAmount = state.cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const normalizedTables = useMemo(() => {
    const map = new Map<string, BasicTable>();

    tables.forEach((t) => {
      if (!t?.id) return;

      map.set(t.id, {
        ...t,
        isAvailable: t.isAvailable === true,
      });
    });

    return Array.from(map.values());
  }, [tables]);

  const filteredTables = useMemo(() => {
    if (state.filter === "available") {
      return normalizedTables.filter((t) => t.isAvailable);
    }

    if (state.filter === "occupied") {
      return normalizedTables.filter((t) => !t.isAvailable);
    }

    return normalizedTables;
  }, [normalizedTables, state.filter]);

  const stats = useMemo(
    () => ({
      total: normalizedTables.length,
      available: normalizedTables.filter((t) => t.isAvailable).length,
      occupied: normalizedTables.filter((t) => !t.isAvailable).length,
    }),
    [normalizedTables]
  );

  const occupancyRate = stats.total > 0 ? Math.round((stats.occupied / stats.total) * 100) : 0;
  const selectedTableMeta = useMemo(
    () => normalizedTables.find((table) => table.id === state.selectedTableId) || null,
    [normalizedTables, state.selectedTableId]
  );
  const selectedTableName = selectedTableMeta?.tableLabel || selectedTableMeta?.name || "";

  const handleTableClick = useCallback(async (table: BasicTable) => {
    dispatch({ type: "SET_SELECTED_TABLE", payload: table.id });
    dispatch({ type: "SET_PREVIOUS_VIEW", payload: "home" });

    if (table.isAvailable) {
      dispatch({ type: "SET_OPEN_CUSTOMER_FORM", payload: true });
    } else {
      dispatch({ type: "SET_VIEW", payload: "table-orders" });
    }
  }, []);

  const handleCustomerDetailsSubmit = useCallback(
    async (details: CustomerDetails) => {
      try {
        const payload = {
          id: details.id || "",
          tableId: (state.selectedTableId || "").toString(),
          customerName: details.customerName,
          customerEmail: details.customerEmail,
          customerMobile: details.customerMobile,
          totalGuest: details.customerCount,
          waiterName: details.waiterName,
        };
        const response = await saveCustomerDetails(payload);
        dispatch({ type: "SET_CUSTOMER_DETAILS", payload: response.data });
        dispatch({ type: "SET_OPEN_CUSTOMER_FORM", payload: false });
        dispatch({ type: "SET_PREVIOUS_VIEW", payload: "home" });
        dispatch({ type: "SET_VIEW", payload: "new-order" });
      } catch (submitError) {
        console.error("Failed to save customer details", submitError);
      }
    },
    [state.selectedTableId]
  );

  const handleAddToCart = useCallback(
    (menuItem: any, quantity: number) => {
      const existing = state.cartItems.find((item) => item.id === menuItem.id);
      if (existing) {
        dispatch({
          type: "SET_CART_ITEMS",
          payload: state.cartItems.map((item) =>
            item.id === menuItem.id ? { ...item, quantity: item.quantity + quantity } : item
          ),
        });
      } else {
        dispatch({
          type: "SET_CART_ITEMS",
          payload: [
            ...state.cartItems,
            {
              id: menuItem.id,
              name: menuItem.name,
              price: menuItem.discountPrice || menuItem.price,
              quantity,
              instructions: "",
            },
          ],
        });
      }
    },
    [state.cartItems]
  );

  const handleUpdateCartQuantity = useCallback(
    (itemId: string, quantity: number) => {
      dispatch({
        type: "SET_CART_ITEMS",
        payload: state.cartItems
          .map((item) => (item.id === itemId ? { ...item, quantity } : item))
          .filter((item) => item.quantity > 0),
      });
    },
    [state.cartItems]
  );

  const handleUpdateInstructions = useCallback(
    (itemId: string, instructions: string) => {
      dispatch({
        type: "SET_CART_ITEMS",
        payload: state.cartItems.map((item) => (item.id === itemId ? { ...item, instructions } : item)),
      });
    },
    [state.cartItems]
  );

  const handlePlaceOrder = useCallback(() => {
    dispatch({ type: "SET_CART_ITEMS", payload: [] });
    dispatch({ type: "SET_VIEW", payload: "home" });
    dispatch({ type: "SET_SELECTED_TABLE", payload: null });
    dispatch({ type: "SET_CUSTOMER_DETAILS", payload: null });
  }, []);

  const handleBack = useCallback(() => {
    dispatch({ type: "SET_VIEW", payload: state.previousView });
    if (state.previousView === "home") {
      dispatch({ type: "SET_SELECTED_TABLE", payload: null });
      dispatch({ type: "SET_CUSTOMER_DETAILS", payload: null });
      dispatch({ type: "SET_OPEN_CUSTOMER_FORM", payload: false });
    }
  }, [state.previousView]);

  const handleTableOrderNewOrder = useCallback((details?: CustomerDetails) => {
    dispatch({ type: "SET_CUSTOMER_DETAILS", payload: details || { customerName: "Guest", customerCount: 1 } });
    dispatch({ type: "SET_PREVIOUS_VIEW", payload: "table-orders" });
    dispatch({ type: "SET_VIEW", payload: "new-order" });
  }, []);

  if (state.currentView === "new-order") {
    return (
      <div className="animate-in slide-in-from-right duration-300">
        <div className="flex min-h-full flex-col">
          <div className="flex-1">
            <NewOrderPage
              customerDetails={state.customerDetails || {}}
              tableId={state.selectedTableId || ""}
              tableName={selectedTableName}
              restaurantGuid={restaurantGuid}
              cartItems={state.cartItems}
              onBack={handleBack}
              onAddToCart={handleAddToCart}
              onUpdateCartQuantity={handleUpdateCartQuantity}
            />
          </div>

          <CartOverlay
            itemCount={cartItemCount}
            totalAmount={cartTotalAmount}
            onViewCart={() => dispatch({ type: "SET_VIEW", payload: "cart" })}
            onClearCart={() => dispatch({ type: "SET_CART_ITEMS", payload: [] })}
          />
        </div>
      </div>
    );
  }

  if (state.currentView === "cart") {
    return (
      <div className="animate-in slide-in-from-bottom duration-300">
        <CartPage
          cartItems={state.cartItems}
          restaurantGuid={restaurantGuid}
          tableId={state.selectedTableId || ""}
          tableName={selectedTableName}
          customerDetails={state.customerDetails || {}}
          onBack={() => dispatch({ type: "SET_VIEW", payload: "new-order" })}
          onUpdateQuantity={handleUpdateCartQuantity}
          onUpdateInstructions={handleUpdateInstructions}
          onRemoveItem={(id) =>
            dispatch({ type: "SET_CART_ITEMS", payload: state.cartItems.filter((item) => item.id !== id) })
          }
          onPlaceOrder={handlePlaceOrder}
        />
      </div>
    );
  }

  if (state.currentView === "table-orders" && state.selectedTableId) {
    return (
      <div className="animate-in fade-in duration-300">
        <TableOrderDetails
          tableId={state.selectedTableId}
          tableLabel={selectedTableName || state.fullTableDetails[state.selectedTableId]?.name || state.selectedTableId}
          onBack={() => {
            dispatch({ type: "SET_VIEW", payload: "home" });
            dispatch({ type: "SET_SELECTED_TABLE", payload: null });
          }}
          onBill={() => dispatch({ type: "SET_VIEW", payload: "table-bill" })}
          onNewOrder={handleTableOrderNewOrder}
        />
      </div>
    );
  }

  if (state.currentView === "table-bill" && state.selectedTableId) {
    return (
      <OrderWithBillGen
        tableId={state.selectedTableId}
        type="bill"
        onBillClosed={() => dispatch({ type: "SET_VIEW", payload: "table-orders" })}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-40 border-b border-gray-200/70 bg-gray-100/90 px-3 py-2 backdrop-blur-xl transition-all dark:border-slate-800 dark:bg-gray-900/90 sm:px-4 sm:py-4">
        <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-tr from-orange-500 to-amber-400 text-white shadow-lg shadow-orange-500/20 transition-all sm:h-10 sm:w-10 sm:rounded-xl">
                <FaUtensils className="text-sm sm:text-lg" />
              </div>
              {loading && (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 sm:h-3 sm:w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500 sm:h-3 sm:w-3" />
                </span>
              )}
            </div>

            <div className="min-w-0">
              <h1 className="leading-tight text-base font-black tracking-tight text-gray-900 dark:text-gray-100 sm:text-xl">
                QuickServe
              </h1>
            </div>
          </div>

          <button
            onClick={refresh}
            disabled={loading}
            className={`rounded-full p-2 transition-all active:scale-90 sm:p-2.5 ${loading
              ? "animate-spin bg-gray-100 text-orange-500 dark:bg-gray-800"
              : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400"
              }`}
          >
            <FaSync className="text-xs sm:text-sm" />
          </button>
        </div>

        <div className="mx-auto mt-3 flex w-full max-w-[1600px] flex-col gap-3 sm:mt-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-3 gap-1.5 sm:max-w-xl sm:gap-2">
            <div className="min-w-0 rounded-[1rem] border border-white/70 bg-white/80 px-2 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
              <p className="truncate text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400 sm:text-[9px]">
                Total
              </p>
              <p className="mt-0.5 truncate text-[15px] font-black leading-none text-slate-900 dark:text-slate-100 sm:text-[18px]">
                {stats.total}
              </p>
            </div>

            <div className="min-w-0 rounded-[1rem] border border-emerald-100 bg-emerald-50/80 px-2 py-2 shadow-sm dark:border-emerald-500/20 dark:bg-emerald-500/10">
              <p className="truncate text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300 sm:text-[9px]">
                Ready
              </p>
              <p className="mt-0.5 truncate text-[15px] font-black leading-none text-emerald-800 dark:text-emerald-200 sm:text-[18px]">
                {stats.available}
              </p>
            </div>

            <div className="min-w-0 rounded-[1rem] border border-orange-100 bg-orange-50/90 px-2 py-2 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10">
              <p className="truncate text-[11px] font-bold uppercase tracking-[0.16em] text-orange-700 dark:text-orange-300 sm:text-[9px]">
                Live
              </p>
              <p className="mt-0.5 truncate text-[15px] font-black leading-none text-orange-800 dark:text-orange-200 sm:text-[18px]">
                {occupancyRate}%
              </p>
            </div>
          </div>

          <div className="flex touch-pan-x gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 sm:gap-3 lg:justify-end">
            {[
              { id: "all", label: "All", count: stats.total, color: "bg-gray-400" },
              { id: "available", label: "Ready", count: stats.available, color: "bg-emerald-500" },
              { id: "occupied", label: "Dining", count: stats.occupied, color: "bg-orange-500" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => dispatch({ type: "SET_FILTER", payload: tab.id as FilterType })}
                className={`flex-none whitespace-nowrap rounded-xl px-3 py-1.5 text-[11px] font-bold transition-all sm:rounded-2xl sm:px-5 sm:py-2.5 sm:text-sm ${state.filter === tab.id
                  ? "scale-[0.98] bg-gray-900 text-gray-100 shadow-md dark:scale-105 dark:bg-gray-100 dark:text-gray-900"
                  : "border border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                  }`}
              >
                <span className="flex items-center gap-1.5 sm:gap-2.5">
                  <span className={`h-1.5 w-1.5 rounded-full sm:h-2 sm:w-2 ${tab.color}`} />
                  {tab.label}
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[9px] sm:text-[11px] ${state.filter === tab.id ? "bg-gray-100/20 text-gray-100" : "bg-gray-100 dark:bg-gray-900"
                      }`}
                  >
                    {tab.count}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto flex-1 w-full max-w-[1600px] px-3 pb-24 pt-3 sm:px-4 sm:pt-4">
        {error && (
          <div className="mb-4 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            <span>!</span>
            <span>{error}</span>
          </div>
        )}

        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Tables</p>
          </div>
        </div>

        <div className="grid touch-pan-y grid-cols-2 gap-2 min-[380px]:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
          {filteredTables.map((table) => (
            <div key={table.id} className="transform transition-transform duration-100 active:scale-95 @container">
              <TableCard table={table} onOpen={handleTableClick} />
            </div>
          ))}
        </div>

        {filteredTables.length === 0 && !loading && (
          <div className="py-20 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-900 dark:text-gray-500">
              <FaUtensils size={24} />
            </div>
            <p className="font-medium text-gray-500 dark:text-gray-400">No {state.filter} tables found</p>
          </div>
        )}
      </main>

      <CustomerDetailsForm
        open={state.openCustomerForm}
        tableName={selectedTableName || state.selectedTableId || ""}
        onClose={() => dispatch({ type: "SET_OPEN_CUSTOMER_FORM", payload: false })}
        onSubmit={handleCustomerDetailsSubmit}
      />

      <MoveTableModal
        open={state.openMoveModal}
        fromTable={selectedTable}
        availableTables={tables}
        isLoading={state.moveLoading}
        onClose={() => dispatch({ type: "SET_OPEN_MOVE_MODAL", payload: false })}
        onConfirm={async (toTableId) => {
          dispatch({ type: "SET_MOVE_LOADING", payload: true });
          try {
            if (!state.selectedTableId) return;
            await moveTable(state.selectedTableId, toTableId);
            dispatch({ type: "SET_SELECTED_TABLE", payload: null });
            dispatch({ type: "SET_FULL_TABLE_DETAILS", payload: { ...state.fullTableDetails } });
            delete state.fullTableDetails[state.selectedTableId];
            dispatch({ type: "SET_OPEN_MOVE_MODAL", payload: false });
          } finally {
            dispatch({ type: "SET_MOVE_LOADING", payload: false });
          }
        }}
      />
    </div>
  );
};

export default React.memo(QuickServe);
